
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import {
  StatsType, UserTypeEnum,
} from '@/types';
import {
  defineComponent, onBeforeMount, Ref, ref, toRefs, watch,
} from 'vue';

import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useI18n } from 'vue-i18n';
import { showToastError, showToastSuccess } from '@/helpers';
import ImportModal from '@/components/import/ImportModal.vue';
import {
  UsersPagesConfigurationInputItem,
  UsersPagesConfigurationUpdateInputItem,
  UsersPagesSubscriptions,
} from '@/types/generated-types/graphql';
import { findPermission, UserState } from '@/composables/User';
import {
  DuplicateUsersPagesConfiguration,
  GetUsersPagesConfiguration,
  UpdateUsersPagesConfiguration,
} from '@/composables/Statistics/usersPagesConfiguration';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import StatsModelSelector from '@/components/stats/StatsModelSelector.vue';
import { useStore } from '@/store';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SecureSpmButton from '@/components/spm-primevue/SecureSpmButton.vue';
import {
  getUserStatsPagesV2,
} from '@/composables/Statistics/usersPagesSubscriptions';
import { NestApiType, nestCall } from '@/composables/nestApi';
import moment from 'moment';
import OverlayPanel from 'primevue/overlaypanel';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import defaultLanguages from '@/configs/languages';
import SendTestModal, { Suggestion } from '@/components/modals/SendTestModal.vue';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import useStatsActions from './StatsActions/useStatsActions';

export default defineComponent({
  name: 'StatsListV2',

  components: {
    ImportModal,
    SendTestModal,
    LocalizedTextInput,
    StatsModelSelector,
    SpmTableAPI,
    SpmDateDisplay,
    ConfirmDialog,
    TabView,
    TabPanel,
    SpmButton,
    SecureSpmButton,
    OverlayPanel,
  },

  setup() {
    const { t } = useI18n();
    const confirm = useConfirm();
    const store = useStore();
    const userLang = ref(UserState.user.lang);
    const emailTableRef = ref();
    const statsTableRef = ref();
    const activeTab = ref(StatsType.STATS);
    const showStatsModelSelector = ref(false);
    const renameOverlayRef = ref();
    const statsModelName = ref();
    const currentRowData = ref();
    const selectedReport = ref();
    const isImportModalVisible = ref(false);
    const idUser = ref(UserState.user.id);
    const userType = ref(UserState.user.userType);

    const pagesSubscriptions: Ref<UsersPagesSubscriptions[]> = ref([]);
    const pagesSubscriptionsById: Ref<number[]> = ref([]);

    const setLoading = async (loading: boolean) => {
      const currentTableRef = activeTab.value === StatsType.STATS ? statsTableRef : emailTableRef;
      if (currentTableRef.value) {
        await currentTableRef.value.setLoading(loading);
      }
    };

    // API call configuration
    const apiCall = ref<ApiCallConfig | undefined>({
      type_api: NestApiType.CLIENT_API,
      path: '/stats/users-pages-configuration',
      secure: true,
    });

    const statsFilters: SpmApiFilter[] = [
      {
        field: 'type',
        values: StatsType.STATS,
        operator: SpmFilterOperator.EQUALS,
      },
      {
        field: 'deleted',
        values: 0,
        operator: SpmFilterOperator.EQUALS,
      },
      {
        field: 'user_id',
        values: UserState.user.id,
        operator: SpmFilterOperator.EQUALS,
      },
      {
        field: 'user_type',
        values: UserState.user.userType,
        operator: SpmFilterOperator.EQUALS,
      },
    ];

    if (UserState.activeShop && UserState.user && UserState.user.id !== UserState.activeShop.idUser) {
      statsFilters.push({
        field: 'admin_user_id',
        values: UserState.activeShop.idUser,
        operator: SpmFilterOperator.NOT_IN,
      });
    }

    const emailFilters: SpmApiFilter[] = [
      {
        field: 'type',
        values: StatsType.EMAIL,
        operator: SpmFilterOperator.EQUALS,
      },
      {
        field: 'deleted',
        values: 0,
        operator: SpmFilterOperator.EQUALS,
      },
      {
        field: 'user_id',
        values: UserState.user.id,
        operator: SpmFilterOperator.EQUALS,
      },
      {
        field: 'user_type',
        values: UserState.user.userType,
        operator: SpmFilterOperator.EQUALS,
      },
    ];

    // Ajouter le filtre d'exclusion admin_user_id si l'utilisateur n'est pas le propriétaire du shop
    if (UserState.activeShop && UserState.user && UserState.user.id !== UserState.activeShop.idUser) {
      emailFilters.push({
        field: 'admin_user_id',
        values: UserState.activeShop.idUser,
        operator: SpmFilterOperator.NOT_IN,
      });
    }

    const showImportModal = () => {
      isImportModalVisible.value = true;
    };

    // Watch import modal visibility to reload table when modal closes
    watch(isImportModalVisible, (newValue, oldValue) => {
      // Reload table when modal closes (visible -> hidden)
      if (oldValue === true && newValue === false && statsTableRef.value?.reloadData) {
        statsTableRef.value.reloadData();
      }
    });

    const handleImportSuccess = () => {
      // Reload the stats table after successful import
      if (statsTableRef.value) {
        statsTableRef.value.reloadData();
      }
    };

    const isJsonString = (str: string) => str.trim().startsWith('{') && str.trim().endsWith('}');

    const showRenameOverlay = (usersPagesConfiguration: UsersPagesConfigurationUpdateInputItem, event: any) => {
      if (usersPagesConfiguration.page_name) {
        if (typeof usersPagesConfiguration.page_name === 'object' && usersPagesConfiguration.page_name !== null) {
          statsModelName.value = usersPagesConfiguration.page_name;
        } else if (typeof usersPagesConfiguration.page_name === 'string' && isJsonString(usersPagesConfiguration.page_name)) {
          statsModelName.value = JSON.parse(usersPagesConfiguration.page_name);
        } else {
          statsModelName.value = usersPagesConfiguration.page_name;
        }
      }
      currentRowData.value = usersPagesConfiguration;
      renameOverlayRef.value.toggle(event);
    };

    const UpdateRows = (usersPagesConfig: UsersPagesConfigurationUpdateInputItem[], successMsg: string, errorMsg: string) => {
      UpdateUsersPagesConfiguration(usersPagesConfig).then((result) => {
        if (result.status && result.id) {
          showToastSuccess(successMsg);
          store.commit('statsEditor/setRefreshNavigationBar', true);
        } else {
          showToastError(errorMsg);
        }
      }).catch(() => {
        showToastError(errorMsg);
      });
    };

    const getPagesSubscriptionsForUser = async () => {
      // Get users pages subscriptions
      const result = await getUserStatsPagesV2(UserState.user.id, UserState.user.userType, null, UserState.activeShop?.idUser);

      if (result && result.items && result.items.length) {
        pagesSubscriptions.value = result.items;
        pagesSubscriptionsById.value = result.items.map((subscription: UsersPagesSubscriptions) => subscription.id_users_pages_configuration);
      }
    };

    const deleteConfirmationDialog = (usersPagesConfig: any) => {
      confirm.require({
        message: t('stats.pages.delete.body'),
        header: t('stats.pages.delete.title'),
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('stats.pages.delete.validate'),
        rejectLabel: t('stats.pages.delete.cancel'),
        accept: () => {
          const usersPagesConfInput: any = {
            id_users_pages_configuration: usersPagesConfig.id,
            deleted: true,
            date_modification: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
          };

          UpdateRows([usersPagesConfInput], t('stats.pages.delete.success'), t('stats.pages.delete.error'));
          // Rafraîchir la table active
          if (activeTab.value === StatsType.STATS && statsTableRef.value) {
            statsTableRef.value.reloadData();
          } else if (activeTab.value === StatsType.EMAIL && emailTableRef.value) {
            emailTableRef.value.reloadData();
          }
          store.commit('statsEditor/setRefreshNavigationBar', true);
        },
      });
    };

    const duplicateConfirmationDialog = (usersPagesConfig: any) => {
      confirm.require({
        message: t('stats.pages.duplicate.body'),
        header: t('stats.pages.duplicate.title'),
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('stats.pages.duplicate.validate'),
        rejectLabel: t('stats.pages.duplicate.cancel'),
        accept: async () => {
          const record = await GetUsersPagesConfiguration(usersPagesConfig.id,
            ['page_name', 'configuration', 'extra_data', 'type']);
          if (record.err || record.item == null) {
            return showToastError(t('stats.pages.duplicate.error'));
          }

          let adminUserId = null;
          if (UserState.user.userType === UserTypeEnum.ADMIN) {
            adminUserId = UserState.user.id;
          } else if (UserState.activeShop) {
            adminUserId = UserState.activeShop?.idUser;
          }

          const usersPagesConfInput: UsersPagesConfigurationInputItem = {
            owner: 'user',
            page_name: record.item.page_name,
            navigation_name: record.item.navigation_name ?? record.item.page_name,
            admin_user_id: adminUserId,
            created_by_user_id: UserState.user.id,
            created_by_user_type: UserState.user.userType,
            configuration: record.item.configuration,
            extra_data: record.item.extra_data,
            type: record.item.type,
            date_creation: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
            date_modification: new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }),
          };

          return DuplicateUsersPagesConfiguration([usersPagesConfInput]).then((result) => {
            if (result.status && result.id) {
              showToastSuccess(t('stats.pages.duplicate.success'));
              // Rafraîchir la table active
              if (activeTab.value === StatsType.STATS && statsTableRef.value) {
                statsTableRef.value.reloadData();
              } else if (activeTab.value === StatsType.EMAIL && emailTableRef.value) {
                emailTableRef.value.reloadData();
              }
              store.commit('statsEditor/setRefreshNavigationBar', true);
            } else {
              showToastError(t('stats.pages.duplicate.error'));
            }
          }).catch(() => {
            showToastError(t('stats.pages.duplicate.error'));
          });
        },
      });
    };

    const tabChange = (tabIndex: any) => {
      switch (tabIndex.index) {
        case 0:
          activeTab.value = StatsType.STATS;
          break;
        case 1:
          activeTab.value = StatsType.EMAIL;
          break;
        default:
          activeTab.value = StatsType.STATS;
          break;
      }
    };

    // Use the composable for row actions
    const { rowActions } = useStatsActions({
      activeTab,
      selectedReport,
      showRenameOverlay,
      duplicateConfirmationDialog,
      deleteConfirmationDialog,
    });

    const closeRenameOverlay = () => {
      renameOverlayRef.value.hide();
    };

    const columns: SpmColumnConfig[] = [
      // Hidden: ID for selection/actions
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      // Composite: page_name with dates
      {
        id: 'page_info',
        header: t('stats.pages.headers.pageName'),
        fields: [{
          name: 'page_name',
          label: t('stats.pages.headers.pageName'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('stats.pages.headers.dateCreated'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('stats.pages.headers.dateModified'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      // Hidden: type field
      {
        id: 'type',
        header: '',
        fields: ['type'],
        hidden: true,
      },
      // Hidden: owner field
      {
        id: 'owner',
        header: '',
        fields: ['owner'],
        hidden: true,
      },
    ];

    const closeStatsModelSelector = () => {
      showStatsModelSelector.value = false;
    };

    const selectStatsModel = async (pageId: number) => {
      await store.dispatch('statsEditor/editPage',
        { pageId });
      closeStatsModelSelector();
      store.commit('general/hideTheSpinner');
    };

    const showStatsModelSelectorHandler = () => {
      showStatsModelSelector.value = true;
    };

    function isJSONObject(variable: any) {
      try {
        JSON.parse(variable);
        return true;
      } catch (e) {
        return false;
      }
    }

    const updateStatsModel = async (field: string, data: any, jsonData: any, originalJsonObject: any) => {
      const modifiedData = originalJsonObject;

      if (!isJSONObject(jsonData)) {
        modifiedData[userLang.value] = data[field];
      }

      // Gérer les deux formats: API Nest (id) et GraphQL (id_users_pages_configuration)
      const configId = data.id_users_pages_configuration || data.id;

      const usersPagesConfig: UsersPagesConfigurationUpdateInputItem = {
        id_users_pages_configuration: configId,
        [field]: JSON.stringify(modifiedData),
        date_modification: moment().format('YYYY-MM-DD HH:mm:ss'),
      };

      UpdateUsersPagesConfiguration([usersPagesConfig]).then((result) => {
        if (result.status && result.id) {
          showToastSuccess(t('stats.actions.rename.nameUpdated'));
          // Rafraîchir la table active
          if (activeTab.value === StatsType.STATS && statsTableRef.value) {
            statsTableRef.value.reloadData();
          } else if (activeTab.value === StatsType.EMAIL && emailTableRef.value) {
            emailTableRef.value.reloadData();
          }
          store.commit('statsEditor/setRefreshNavigationBar', true);
        } else {
          showToastError(t('errorMessages.updateFailed'));
        }
      }).catch((err: Error) => {
        showToastError(err.message);
      });
    };

    const renameStatsModel = () => {
      updateStatsModel('page_name', currentRowData.value, JSON.stringify(toRefs(statsModelName.value)), statsModelName.value);
      closeRenameOverlay();
    };

    onBeforeMount(() => {
      getPagesSubscriptionsForUser();
    });

    const getLanguageValue = (columnValue: any) => {
      // Si c'est déjà un objet (pas une string)
      if (typeof columnValue === 'object' && columnValue !== null) {
        return columnValue[userLang.value] || columnValue.fr || columnValue.en || '';
      }
      // Si c'est une string JSON
      if (typeof columnValue === 'string' && isJsonString(columnValue)) {
        const result = JSON.parse(columnValue);
        return result[userLang.value] || result.fr || result.en || '';
      }
      // Si c'est une string simple
      return columnValue || '';
    };

    /**
     * Send test report by e-mail
     * @param params
     */
    const sendTestReport = async (params: Suggestion[]) => {
      nestCall('post', NestApiType.ESR, '/esr/send-test', {}, '', {
        idShop: UserState.activeShop?.id ?? 0,
        idReport: selectedReport.value,
        addresses: params,
      }).then(async (result) => {
        if (result && result.success) {
          await showToastSuccess('emailReportSent');
        } else {
          await showToastError('GENERIC_ERROR');
        }

        selectedReport.value = null;
      });
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        setLoading(true);
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          selection_type: payload.selectAllMode ? 'all' : 'manual',
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          user_id: idUser.value,
          user_type: userType.value,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        const response = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/export/init',
          {},
          '',
          exportRequest,
          true,
        );

        if (response && !response.error) {
          await showToastSuccess(t('myLists.manage.customers.export.success'));
        } else {
          await showToastError(t('myLists.manage.customers.export.error'));
        }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myLists.manage.customers.export.error'));
      } finally {
        setLoading(false);
      }
    };

    return {
      t,
      columns,
      emailFilters,
      statsFilters,
      statsTableRef,
      emailTableRef,
      rowActions,
      tabChange,
      closeStatsModelSelector,
      selectStatsModel,
      showStatsModelSelectorHandler,
      showStatsModelSelector,
      StatsType,
      findPermission,
      getLanguageValue,
      statsModelName,
      renameOverlayRef,
      renameStatsModel,
      closeRenameOverlay,
      defaultLanguages,
      sendTestReport,
      isImportModalVisible,
      showImportModal,
      handleImportSuccess,
      idUser,
      userType,
      apiCall,
      handleExport,
    };
  },
});
